import { useState } from 'react';
import {
  customerConsents,
  recordConsent,
  withdrawConsent,
  type ConsentRecord,
  type ConsentsPayload,
} from '../api';
import { type IdentityLocale, type IdentityTranslate } from '../i18n';
import {
  Button,
  Card,
  DataTable,
  Field,
  Notice,
  StatusBadge,
  TextInput,
  Moment,
  type Column,
} from '../../../shell/kit';
import { CustomerPicker } from '../../../shell/pickers';
import { useToast } from '../../../shell/toast';
import { useConfirm } from '../../../shell/confirm';
import { errorText } from '../../../shell/errors';

export function ConsentsView({
  t,
  locale,
  canAnonymize,
  canRecord,
  customerId,
  onCustomerChange,
}: {
  t: IdentityTranslate;
  locale: IdentityLocale;
  canAnonymize: boolean;
  /** Право `consent.manage`: записать согласие вручную может управляющий и администратор. */
  canRecord: boolean;
  /** Клиент выбран на уровне модуля, чтобы переключение вкладок его не стирало. */
  customerId: string;
  onCustomerChange: (id: string) => void;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const [customerLabel, setCustomerLabel] = useState('');
  const [data, setData] = useState<ConsentsPayload | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const fail = (err: unknown): void => {
    const shown = errorText(err);
      toast.error(shown.message, shown.code);
  };

  const load = async (id = customerId): Promise<void> => {
    if (!id) return;
    try {
      setData(await customerConsents(id.trim()));
    } catch (err) {
      fail(err);
    }
  };

  const act = async (fn: () => Promise<unknown>, message?: string): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      if (message) toast.ok(message);
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Обезличивание необратимо: имя, телефон и связи стираются навсегда. Раньше это
   * был один клик по всегда активной кнопке, единственной защитой было обязательное
   * поле «Основание», а ответ об успехе улетал в баннер вверху страницы — за
   * пределы экрана. Для самой разрушительной операции системы этого мало.
   */
  const anonymize = async (): Promise<void> => {
    if (reason.trim().length < 3) {
      toast.warn(t('identity.duplicates.needReason'));
      return;
    }
    const ok = await confirm({
      title: t('identity.consents.anonymizeConfirm'),
      body: customerLabel || customerId,
      tone: 'danger',
      typeToConfirm: t('identity.consents.anonymizeWord'),
      confirmLabel: t('identity.consents.anonymize'),
      consequences: [
        t('identity.consents.effectPii'),
        t('identity.consents.effectLinks'),
        t('identity.consents.effectAudit'),
      ],
    });
    if (!ok) return;

    await act(
      () => withdrawConsent(customerId.trim(), reason, true),
      t('identity.consents.anonymized'),
    );
  };

  const columns: ReadonlyArray<Column<ConsentRecord>> = [
    {
      // Сырые ключи data_processing / marketing стояли в таблице при том, что
      // карточки выше называют то же самое «Обработка данных» и «Маркетинг».
      key: 'kind',
      header: t('identity.duplicates.kind'),
      render: (r) =>
        r.kind === 'marketing'
          ? t('identity.consents.marketing')
          : t('identity.consents.dataProcessing'),
    },
    {
      // Отдельная колонка события. Строка-отзыв показывала дату в колонке
      // «Выдано» и читалась как «выдано согласие, которое не действует».
      key: 'event',
      header: t('identity.consents.event'),
      render: (r) =>
        r.granted ? (
          <StatusBadge tone={r.active ? 'ok' : 'neutral'}>
            {r.active ? t('identity.consents.eventGranted') : t('identity.consents.eventExpired')}
          </StatusBadge>
        ) : (
          <StatusBadge tone="warn">{t('identity.consents.eventWithdrawn')}</StatusBadge>
        ),
    },
    { key: 'version', header: t('identity.consents.version'), render: (r) => r.textVersion ?? '—' },
    { key: 'channel', header: t('identity.consents.channel'), render: (r) => r.channel ?? '—' },
    // Строка отзыва — это новая запись с granted = false, и её момент хранится в
    // grantedAt. Показанный как есть, он попадал в колонку «Выдано», а «Отозвано»
    // оставалась пустой (QA.md, Q14). Для такой строки время уходит во вторую колонку.
    {
      key: 'grantedAt',
      header: t('identity.consents.grantedAt'),
      render: (r) => <Moment iso={r.granted ? r.grantedAt : null} />,
    },
    {
      key: 'withdrawnAt',
      header: t('identity.consents.withdrawnAt'),
      render: (r) => <Moment iso={r.granted ? r.withdrawnAt : r.grantedAt} />,
    },
  ];

  return (
    <div className="space-y-4">
      <Notice tone="info">{t('identity.consents.marketingGate')}</Notice>

      <Card title={t('identity.consents.title')}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-72 flex-1">
            <Field
              label={t('identity.consents.customer')}
              hint={t('identity.consents.customerHint')}
            >
              <CustomerPicker
                value={customerId}
                onChange={(id, hit) => {
                  onCustomerChange(id);
                  setCustomerLabel(hit?.displayName ?? hit?.phone ?? id);
                  setData(null);
                  if (id) void load(id);
                }}
              />
            </Field>
          </div>
          <Button onClick={() => void load()} disabled={!customerId}>
            {t('identity.consents.load')}
          </Button>
        </div>
      </Card>

      {data ? (
        <>
          <Card>
            <div className="grid gap-3 md:grid-cols-2">
              {(['data_processing', 'marketing'] as const).map((kind) => {
                const state = data.current[kind];
                const label =
                  kind === 'marketing'
                    ? t('identity.consents.marketing')
                    : t('identity.consents.dataProcessing');
                return (
                  <div key={kind} className="rounded border border-line p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-medium text-ink">{label}</span>
                      <StatusBadge tone={state?.active ? 'ok' : 'neutral'}>
                        {state?.active
                          ? t('identity.consents.active')
                          : t('identity.consents.inactive')}
                      </StatusBadge>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        disabled={busy || !canRecord}
                        onClick={() =>
                          void act(
                            () => recordConsent(customerId.trim(), kind, true, 'v1'),
                            t('identity.consents.granted'),
                          )
                        }
                      >
                        {t('identity.consents.grant')}
                      </Button>
                      <Button
                        tone="danger"
                        disabled={busy || !state?.active}
                        onClick={() =>
                          void act(
                            () =>
                              withdrawConsent(
                                customerId.trim(),
                                'отзыв через админку',
                                false,
                                kind,
                              ),
                            t('identity.consents.withdrawn'),
                          )
                        }
                      >
                        {t('identity.consents.withdrawOne')}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card title={t('identity.consents.history')}>
            <DataTable
              loading={busy}
              rows={data.history}
              columns={columns}
              empty={t('identity.common.nothing')}
              rowKey={(r) => r.id}
            />
          </Card>

          {canAnonymize ? (
            <Card title={t('identity.consents.anonymize')}>
              <Notice tone="warn" title={t('identity.consents.anonymizeWarn')}>
                {t('identity.consents.anonymizeIrreversible')}
              </Notice>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <div className="min-w-72 flex-1">
                  <Field label={t('identity.duplicates.reason')} required>
                    <TextInput value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                </div>
                <Button
                  tone="danger"
                  disabled={busy || reason.trim().length < 3}
                  onClick={() => void anonymize()}
                >
                  {t('identity.consents.anonymize')}
                </Button>
              </div>
            </Card>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
